import type React from 'react'
import { useCallback, useTransition } from 'react'
import { useIntl } from 'react-intl'

import { usePromotionReport } from '@/api/hooks/usePromotionReport'
import { Button } from '@/components/atoms/Button/Button.tsx'
import { NavGroup, NavGroupItem } from '@/components/atoms/NavGroup/NavGroup.tsx'
import { type Promotion } from '@/components/model/Promotion'
import { AdminHeader, type AdminHeaderProps } from '@/components/organisms/AdminHeader/AdminHeader.tsx'
import { PromotionTable, type PromotionTableProps } from '@/components/organisms/PromotionTable/PromotionTable.tsx'
import { downloadFromBase64 } from '@/lib/download'

export interface AdminPromotionViewProps
  extends Omit<AdminHeaderProps, 'regionName'>, Omit<PromotionTableProps, 'promotions' | 'title'> {
  nationalPromotions?: Promotion[]
  fallbackPromotions?: Promotion[]
  onAddPartnerPromotion: () => void | Promise<void>
  onAddDefaultPromotion: () => void | Promise<void>
}

export const AdminPromotionView: React.FC<AdminPromotionViewProps> = ({
  nationalPromotions,
  fallbackPromotions,
  onAddPartnerPromotion,
  onAddDefaultPromotion,
  onBackToPiloteClicked,
  onRowClick,
}) => {
  const intl = useIntl()
  const [isAddPartnerPromotionPending, startAddPartnerPromotionTransition] = useTransition()
  const addPartnerPromotion = useCallback(() => {
    startAddPartnerPromotionTransition(onAddPartnerPromotion)
  }, [onAddPartnerPromotion, startAddPartnerPromotionTransition])
  const [isAddDefaultPromotionPending, startAddDefaultPromotionTransition] = useTransition()
  const addDefaultPromotion = useCallback(() => {
    startAddDefaultPromotionTransition(onAddDefaultPromotion)
  }, [onAddDefaultPromotion, startAddDefaultPromotionTransition])

  const { mutateAsync: getReport, isPending: isDownloadPending } = usePromotionReport()

  const handleDownload = useCallback(async () => {
    const base64Data = await getReport({})
    downloadFromBase64(
      base64Data,
      'rapport_promotions.xlsx',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    )
  }, [getReport])

  return (
    <div className="flex h-full flex-col">
      <AdminHeader onBackToPiloteClicked={onBackToPiloteClicked} />

      <div className="flex flex-1 flex-col gap-4 overflow-hidden p-4 pl-16">
        <div className="flex min-h-12 items-start justify-between">
          <h1 className="text-xl text-gray-900 select-none">
            {intl.formatMessage({ id: 'promotions.national.title' })}
          </h1>
          <div className="flex items-center gap-4">
            <Button size="sm" onClick={handleDownload} loading={isDownloadPending}>
              {intl.formatMessage({ id: 'promotions.actions.statistics' })}
            </Button>
            <Button
              size="sm"
              onClick={addPartnerPromotion}
              loading={isAddPartnerPromotionPending}
              disabled={isAddDefaultPromotionPending}>
              {intl.formatMessage({ id: 'promotions.actions.add' })}
            </Button>
          </div>
        </div>
        <NavGroup className="self-start">
          <NavGroupItem
            label={intl.formatMessage({ id: 'promotions.link.national' })}
            from="/promotions/$initiativeId/placements"
            to="/promotions/$initiativeId/national"
          />
          <NavGroupItem
            label={intl.formatMessage({ id: 'promotions.link.placements' })}
            from="/promotions/$initiativeId/national"
            to="/promotions/$initiativeId/placements"
          />
        </NavGroup>

        <PromotionTable
          promotions={nationalPromotions}
          title={intl.formatMessage({ id: 'promotions.table.national.partners.title' })}
          onRowClick={onRowClick}
        />

        <div className="flex items-center justify-end pt-8">
          <div className="flex items-center gap-4">
            <Button
              size="sm"
              onClick={addDefaultPromotion}
              loading={isAddDefaultPromotionPending}
              disabled={isAddPartnerPromotionPending}>
              {intl.formatMessage({ id: 'promotions.actions.add' })}
            </Button>
          </div>
        </div>

        <PromotionTable
          promotions={fallbackPromotions}
          title={intl.formatMessage({ id: 'promotions.table.default.visual.title' })}
          onRowClick={onRowClick}
        />
      </div>
    </div>
  )
}
